const GIB = 1024 ** 3;

export interface SatelliteArchiveSizeSource {
  satellite?: {
    result?: unknown;
  } | null;
  safeOutputJson?: string | null;
}

export function formatArchiveSizeGiB(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) {
    return '—';
  }
  return `${(bytes / GIB).toFixed(2)} GiB`;
}

export interface SatelliteOverviewParameters {
  name: string | null;
  geometrySource: string | null;
  providers: string | null;
  startDate: string | null;
  endDate: string | null;
  maximumCloudCover: number | null;
  regionCount: number | null;
  regionIds: string[];
}

export function readSatelliteOverviewParameters(assignment: {
  satellite?: { parameters?: unknown } | null;
  safeInputJson?: string | null;
} | null): SatelliteOverviewParameters | null {
  const fromTask = asRecord(assignment?.satellite?.parameters);
  const fromSafe = parseObject(assignment?.safeInputJson);
  const source = fromTask ?? (readText(fromSafe, 'GeometrySource', 'geometrySource') ? fromSafe : null);
  if (!source) {
    return null;
  }
  return {
    name: readText(source, 'name', 'Name'),
    geometrySource: readText(source, 'geometrySource', 'GeometrySource'),
    providers: readText(source, 'providers', 'Providers'),
    startDate: readText(source, 'startDate', 'StartDate'),
    endDate: readText(source, 'endDate', 'EndDate'),
    maximumCloudCover: readOptionalNumber(source, 'maximumCloudCover', 'MaximumCloudCover'),
    regionCount: readOptionalNumber(source, 'regionCount', 'RegionCount'),
    regionIds: readStringList(source, 'regionIds', 'RegionIds'),
  };
}

export interface SatelliteBacklogSizes {
  pendingDownloadSizeInBytes: number | null;
  inProgressSizeInBytes: number | null;
  alreadyDownloadedSizeInBytes: number | null;
}

/** GiB rows for satellite.measure-download-backlog. Found/Downloaded size do not apply. */
export function satelliteMeasureBacklogSizeRows(
  assignment: SatelliteArchiveSizeSource,
): { label: string; value: string }[] {
  const sizes = readSatelliteBacklogSizes(assignment);
  return [
    { label: 'Left to download', value: formatArchiveSizeGiB(sizes.pendingDownloadSizeInBytes) },
    { label: 'Downloading size', value: formatArchiveSizeGiB(sizes.inProgressSizeInBytes) },
    { label: 'Already downloaded', value: formatArchiveSizeGiB(sizes.alreadyDownloadedSizeInBytes) },
  ];
}

export function readSatelliteBacklogSizes(assignment: SatelliteArchiveSizeSource): SatelliteBacklogSizes {
  const output = parseObject(assignment.safeOutputJson);
  return {
    pendingDownloadSizeInBytes: readByteCountFromKeys(output, [
      'PendingDownloadSizeInBytes',
      'pendingDownloadSizeInBytes',
    ]),
    inProgressSizeInBytes: readByteCountFromKeys(output, [
      'InProgressSizeInBytes',
      'inProgressSizeInBytes',
    ]),
    alreadyDownloadedSizeInBytes: readByteCountFromKeys(output, [
      'AlreadyDownloadedSizeInBytes',
      'alreadyDownloadedSizeInBytes',
    ]),
  };
}

export function readSatelliteArchiveSizes(assignment: SatelliteArchiveSizeSource): {
  estimatedSizeInBytes: number | null;
  downloadedSizeInBytes: number | null;
} {
  const result = asRecord(assignment.satellite?.result);
  return {
    estimatedSizeInBytes:
      readByteCount(result, 'estimatedSizeInBytes')
      ?? readByteCountFromOutput(assignment.safeOutputJson, ['EstimatedSizeInBytes', 'estimatedSizeInBytes']),
    downloadedSizeInBytes:
      readByteCount(result, 'downloadedSizeInBytes')
      ?? readByteCountFromOutput(assignment.safeOutputJson, ['DownloadedSizeInBytes', 'downloadedSizeInBytes']),
  };
}

function readByteCountFromOutput(json: string | null | undefined, keys: string[]): number | null {
  if (!json) return null;
  try {
    return readByteCountFromKeys(asRecord(JSON.parse(json)), keys);
  } catch {
    return null;
  }
}

function readByteCountFromKeys(record: Record<string, unknown> | null, keys: string[]): number | null {
  if (!record) return null;
  for (const key of keys) {
    const value = readByteCount(record, key);
    if (value != null) return value;
  }
  return null;
}

function parseObject(json?: string | null): Record<string, unknown> | null {
  if (!json) return null;
  try {
    return asRecord(JSON.parse(json));
  } catch {
    return null;
  }
}

function readText(record: Record<string, unknown> | null, ...keys: string[]): string | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

function readOptionalNumber(record: Record<string, unknown> | null, ...keys: string[]): number | null {
  if (!record) return null;
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return null;
}

function readStringList(record: Record<string, unknown> | null, ...keys: string[]): string[] {
  if (!record) return [];
  for (const key of keys) {
    const value = record[key];
    if (!Array.isArray(value)) continue;
    return value.filter((item): item is string => typeof item === 'string' && item.length > 0);
  }
  return [];
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readByteCount(record: Record<string, unknown> | null, key: string): number | null {
  if (!record) return null;
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null;
}
